import EditQuestion from "@/components/EditQuestion/EditQuestion";
import SelectQuestionsDialog from "@/components/SelectQuestions/SelectQuestionsDialog";
import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { useSetPageTitle } from "@/context/PageTitleContext";
import { IEdoQuestion } from "@/shared/model/edo-question.model";
import { IEdoQuizQuestion } from "@/shared/model/edo-quiz-question.model";
import { IEdoQuiz } from "@/shared/model/edo-quiz.model";
import { EdoQuestionTypeEnum } from "@/shared/model/enumerations/edo-question-type.mode";
import { createEntity as createQuizQuestion } from "@/shared/reducers/entities/edo-quiz-question.reducer";
import { getEntity } from "@/shared/reducers/entities/edo-quiz.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import {
  AlignLeft,
  CheckSquare,
  ClipboardList,
  Plus,
  Send,
  Square,
  ToggleLeft,
} from "lucide-react";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Translate } from "react-jhipster";
import { useParams } from "react-router-dom";
import Question from "./components/Question";
import RemoveQuestion from "./components/RemoveQuestion";
import SendQuiz from "./components/SendQuiz";

const QuizRoute = () => {
  const dispatch = useAppDispatch();
  const params = useParams<{ id: string }>();
  const { entity: quiz } = useAppSelector<EntityState<IEdoQuiz>>((state) => state.edoQuiz);
  useSetPageTitle(quiz?.title || "Test", "Dettaglio test");
  const { updateSuccess: updateSuccessQuizQuestion } = useAppSelector<
    EntityState<IEdoQuizQuestion>
  >((state) => state.edoQuizQuestion);
  const [questions, setQuestions] = useState<IEdoQuizQuestion[]>([]);
  const [editQuestionDialog, setEditQuestionDialog] = useState<{
    open: boolean;
    entity: IEdoQuestion | null;
    type: EdoQuestionTypeEnum | null;
  }>({ open: false, entity: null, type: null });
  const [questionsToAdd, setQuestionsToAdd] = useState<IEdoQuestion[]>([]);
  const [selectQuestionsDialog, setSelectQuestionsDialog] = useState<{
    open: boolean;
  }>({ open: false });
  const [removeQuestionDialog, setRemoveQuestionDialog] = useState<{
    open: boolean;
    entity: IEdoQuizQuestion | null;
  }>({ open: false, entity: null });
  const [sendQuizDialog, setSendQuizDialog] = useState<{
    open: boolean;
    entity: IEdoQuiz | null;
  }>({ open: false, entity: null });

  const getQuizQuestions = async () => {
    const response = await axios.get<{ data: IEdoQuizQuestion[] }>(
      `/quizzes/${params.id}/questions`,
      { params: { sort: "updatedAt,ASC" } }
    );
    setQuestions(response.data.data);
  };

  const handleQuestionCreated = async () => {
    getQuizQuestions();
  };

  const handleAddQuestionsToQuiz = async (questionsToAddList: IEdoQuestion[]) => {
    await Promise.all(
      questionsToAddList.map((question) =>
        dispatch(createQuizQuestion({ question, quiz }))
      )
    );
    setQuestionsToAdd([]);
    getQuizQuestions();
  };

  useEffect(() => {
    dispatch(getEntity(params.id));
    getQuizQuestions();
  }, []);

  useEffect(() => {
    if (updateSuccessQuizQuestion) {
      getQuizQuestions();
    }
  }, [updateSuccessQuizQuestion]);

  const totalScore = questions.reduce(
    (acc, q) => acc + (q.question?.totalScore ?? 0),
    0
  );

  return (
    <>
      <div className="mx-auto grid max-w-5xl gap-5 px-4 py-6 sm:px-6 lg:px-8">
        <Card className="overflow-hidden p-0">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6 sm:p-6">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-mockup-text-light)]">
                <ClipboardList className="h-3.5 w-3.5" />
                Test
              </div>
              <h1 className="mt-2 font-heading text-2xl font-bold leading-tight text-[var(--color-mockup-text)] sm:text-3xl">
                {quiz?.title || "—"}
              </h1>
              {quiz?.description && (
                <p className="mt-2 max-w-2xl text-sm text-[var(--color-mockup-text-mid)]">
                  {quiz.description}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[var(--color-mockup-text-light)]">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 font-semibold text-primary">
                  {questions.length}{" "}
                  {questions.length === 1 ? "domanda" : "domande"}
                </span>
                {questions.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-mockup-amber-light)] px-2.5 py-1 font-semibold text-[var(--color-mockup-amber)]">
                    {totalScore} {totalScore === 1 ? "punto" : "punti"}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4" />
                    Aggiungi domanda
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() =>
                      setEditQuestionDialog({
                        open: true,
                        entity: null,
                        type: EdoQuestionTypeEnum.SINGLE_CHOICE,
                      })
                    }
                  >
                    <Square className="h-4 w-4" />
                    Crea domanda a risposta singola
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      setEditQuestionDialog({
                        open: true,
                        entity: null,
                        type: EdoQuestionTypeEnum.MULTIPLE_CHOICE,
                      })
                    }
                  >
                    <CheckSquare className="h-4 w-4" />
                    Crea domanda a risposta multipla
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      setEditQuestionDialog({
                        open: true,
                        entity: null,
                        type: EdoQuestionTypeEnum.TRUE_FALSE,
                      })
                    }
                  >
                    <ToggleLeft className="h-4 w-4" />
                    Crea domanda Vero/Falso
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      setEditQuestionDialog({
                        open: true,
                        entity: null,
                        type: EdoQuestionTypeEnum.FREE_TEXT,
                      })
                    }
                  >
                    <AlignLeft className="h-4 w-4" />
                    Crea domanda a risposta scritta
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSelectQuestionsDialog({ open: true })}
                  >
                    <CheckSquare className="h-4 w-4" />
                    Seleziona domande dall&apos;archivio
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSendQuizDialog({ open: true, entity: quiz })}
              >
                <Send className="h-4 w-4" />
                Invia test
              </Button>
            </div>
          </div>
        </Card>
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between gap-3 border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/40 px-5 py-3 dark:bg-[var(--color-surface-secondary)]/30">
            <h2 className="font-heading text-base font-bold text-[var(--color-mockup-text)]">
              Domande
            </h2>
            {questions.length > 0 && (
              <span className="text-xs font-medium text-[var(--color-mockup-text-light)]">
                {questions.length}{" "}
                {questions.length === 1 ? "domanda" : "domande"} in totale
              </span>
            )}
          </div>
          {questions.length ? (
            <ol className="divide-y divide-[var(--color-mockup-border)]">
              {questions.map((question, index) => (
                <li key={question.id}>
                  <Question
                    index={index + 1}
                    onEdit={(q: IEdoQuestion) =>
                      setEditQuestionDialog({ open: true, entity: q, type: null })
                    }
                    onDelete={() =>
                      setRemoveQuestionDialog({ open: true, entity: question })
                    }
                    question={question.question}
                  />
                </li>
              ))}
            </ol>
          ) : (
            <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <ClipboardList className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                <Translate contentKey="edocendoApp.edoQuestion.home.notFound">
                  No Edo Questions found
                </Translate>
              </p>
              <p className="text-xs text-[var(--color-mockup-text-light)]">
                Aggiungi una domanda dal pulsante in alto per iniziare.
              </p>
            </div>
          )}
        </Card>
      </div>
      <EditQuestion
        open={editQuestionDialog.open}
        question={editQuestionDialog.entity}
        onClose={() =>
          setEditQuestionDialog({ open: false, entity: null, type: null })
        }
        onSuccess={handleQuestionCreated}
        type={editQuestionDialog.type}
        quiz={quiz}
      />
      <SelectQuestionsDialog
        open={selectQuestionsDialog.open}
        onSuccess={handleAddQuestionsToQuiz}
        onClose={() => setSelectQuestionsDialog({ open: false })}
        disabledQuestions={questions.map((el) => el.question)}
        selectedQuestions={questionsToAdd}
        setSelectedQuestions={setQuestionsToAdd}
      />
      <RemoveQuestion
        open={removeQuestionDialog.open}
        question={removeQuestionDialog.entity}
        onClose={() =>
          setRemoveQuestionDialog({ open: false, entity: null })
        }
      />
      <SendQuiz
        open={sendQuizDialog.open}
        quiz={sendQuizDialog.entity}
        onClose={() => setSendQuizDialog({ open: false, entity: null })}
      />
    </>
  );
};

export default QuizRoute;
